import {t,format} from '../i18n.js';
export function initLightbox({ gsap, motion, scope }) {
  const document=scope.document;
  const window=scope.ownerDocument.defaultView;
  const matchMedia=window.matchMedia.bind(window);
  const links = [...document.querySelectorAll("[data-lightbox]")];
  if (!links.length) return;
  const dialog = document.createElement("dialog");
  dialog.className = "aw-lightbox";
  dialog.setAttribute("aria-label", t("Podgląd multimediów"));
  dialog.innerHTML = `<div class="aw-lightbox__panel"><div class="aw-lightbox__bar"><p class="aw-lightbox__count" aria-live="polite"></p><button type="button" class="aw-lightbox__close" aria-label="${t('Zamknij podgląd')}">×</button></div><div class="aw-lightbox__stage"><button type="button" class="aw-lightbox__prev" aria-label="${t('Poprzednie zdjęcie')}">←</button><figure class="aw-lightbox__figure"></figure><button type="button" class="aw-lightbox__next" aria-label="${t('Następne zdjęcie')}">→</button></div><p class="aw-lightbox__caption"></p><p class="sr-only" role="status"></p></div>`;
  document.body.append(dialog);
  const figure = dialog.querySelector("figure"),
    caption = dialog.querySelector(".aw-lightbox__caption");
  const counter = dialog.querySelector(".aw-lightbox__count"),
    status = dialog.querySelector('[role="status"]');
  const prev = dialog.querySelector(".aw-lightbox__prev"),
    next = dialog.querySelector(".aw-lightbox__next");
  let group = [],
    index = 0,
    opener,
    request = 0,
    overflow = "",
    touchStart,
    intrinsic;
  const fitMedia = () => {
    if (!intrinsic) return;
    const mobile = matchMedia("(max-width:767px)").matches;
    const frameWidth = mobile ? 32 : 192;
    const frameHeight = mobile ? 230 : 180;
    const scale = Math.min(
      1,
      (Math.min(window.innerWidth, 1200) - frameWidth) / intrinsic.width,
      Math.max(60, window.innerHeight - frameHeight) / intrinsic.height,
    );
    const width = intrinsic.width * scale;
    const height = intrinsic.height * scale;
    // The dialog follows this photo, not a fixed landscape stage.
    dialog.style.width = `${width + frameWidth}px`;
    figure.style.height = `${intrinsic.video ? Math.max(200, height) : height}px`;
  };
  const show = async (direction = 1) => {
    const current = ++request,
      link = group[index],
      video = link.dataset.youtube;
    prev.hidden = next.hidden = !!video || group.length < 2;
    counter.textContent = video ? "YouTube" : `${index + 1} / ${group.length}`;
    caption.textContent =
      link.dataset.caption || link.querySelector("img")?.alt || "";
    figure.setAttribute("aria-busy", "true");
    status.textContent = t("Ładowanie…");
    if (video) {
      intrinsic = { width: 1280, height: 720, video: true };
      fitMedia();
      const iframe = document.createElement("iframe");
      iframe.title = caption.textContent || t("Film YouTube");
      iframe.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(video)}?autoplay=1&rel=0`;
      iframe.allow =
        "autoplay; encrypted-media; picture-in-picture; fullscreen";
      iframe.allowFullscreen = true;
      iframe.referrerPolicy = "strict-origin-when-cross-origin";
      figure.replaceChildren(iframe);
      figure.classList.add("is-video");
      figure.removeAttribute("aria-busy");
      status.textContent = "";
      return;
    }
    const img = new window.Image();
    img.alt = link.querySelector("img")?.alt || caption.textContent;
    img.src = link.href;
    try {
      await img.decode();
    } catch {
      if (current === request) {
        status.textContent = t("Nie udało się wczytać zdjęcia.");
        figure.removeAttribute("aria-busy");
      }
      return;
    }
    if (current !== request || !dialog.open) return;
    intrinsic = { width: img.naturalWidth, height: img.naturalHeight };
    img.width = img.naturalWidth;
    img.height = img.naturalHeight;
    fitMedia();
    figure.classList.remove("is-video");
    figure.replaceChildren(img);
    figure.removeAttribute("aria-busy");
    status.textContent = format('Zdjęcie %d z %d.',index + 1,group.length);
    if (motion.enabled)
      gsap.fromTo(
        img,
        { opacity: 0, x: direction * 24 },
        {
          opacity: 1,
          x: 0,
          duration: 0.35,
          ease: "power3.out",
          clearProps: "all",
        },
      );
  };
  const move = (step) => {
    index = (index + step + group.length) % group.length;
    show(step);
  };
  const close = () => dialog.close();
  links.forEach((link) =>
    scope.on(link, "click", (e) => {
      e.preventDefault();
      opener = link;
      group = link.dataset.lightboxGroup
        ? links.filter(
            (n) => n.dataset.lightboxGroup === link.dataset.lightboxGroup,
          )
        : [link];
      index = group.indexOf(link);
      overflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      dialog.showModal();
      const thumb = link.querySelector("img");
      if (thumb?.naturalWidth) {
        intrinsic = { width: thumb.naturalWidth, height: thumb.naturalHeight };
        fitMedia();
      }
      dialog.querySelector(".aw-lightbox__close").focus();
      show();
      if (motion.enabled)
        gsap.fromTo(
          dialog.querySelector(".aw-lightbox__panel"),
          { opacity: 0, y: 16 },
          {
            opacity: 1,
            y: 0,
            duration: 0.35,
            ease: "power3.out",
            clearProps: "all",
          },
        );
    }),
  );
  scope.on(prev, "click", () => move(-1));
  scope.on(next, "click", () => move(1));
  scope.on(dialog.querySelector(".aw-lightbox__close"), "click", close);
  scope.on(dialog, "click", (e) => {
    if (e.target === dialog) close();
  });
  scope.on(dialog, "keydown", (e) => {
    if (group.length < 2 || e.target.tagName === "IFRAME") return;
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      e.preventDefault();
      move(e.key === "ArrowRight" ? 1 : -1);
    }
  });
  scope.on(figure, "pointerdown", (e) => {
    if (e.pointerType === "touch") touchStart = e.clientX;
  });
  scope.on(figure, "pointerup", (e) => {
    if (
      touchStart != null &&
      Math.abs(e.clientX - touchStart) > 50 &&
      group.length > 1
    )
      move(e.clientX < touchStart ? 1 : -1);
    touchStart = undefined;
  });
  scope.on(figure, "pointercancel", () => (touchStart = undefined));
  scope.on(window, "resize", () => {
      if (dialog.open) fitMedia();
    }, { passive: true });
  scope.on(dialog, "close", () => {
    request++;
    figure.replaceChildren(); // Removing the iframe stops playback immediately.
    document.body.style.overflow = overflow;
    opener?.focus({ preventScroll: true });
  });
  scope.onDispose(()=>{request++;if(dialog.open){document.body.style.overflow=overflow;dialog.close();}dialog.remove();});
}
